import { useMemo } from 'react';
import { Async } from '@client/new/helpers/types';
import {
  BundlePricing,
  Exact,
  GetInfluencerQuery,
  GetSubscriptionBundlesQuery,
  InputMaybe,
  PublishSubscriptionBundleMutation,
  Scalars,
  UpdateBundleInput,
  UpdateSubscriptionBundlesMutation,
  UpsertOrArchiveSubscriptionBundlePricesInput,
  UpsertOrArchiveSubscriptionBundlePricesMutation,
  useGetSellableContentItemsQuery,
  useGetSubscriptionBundlesQuery,
  usePublishSubscriptionBundleMutation,
  useUpdateSubscriptionBundlesMutation,
  useUpsertOrArchiveSubscriptionBundlePricesMutation,
} from 'generated/graphql';
import {
  toSubscriptionPrograms,
  toSubscriptionBundles,
} from './bundle.model.mapper';
import {
  AllFeatures,
  AllPrograms,
  SubscriptionBundle,
  RawSubscriptionBundle,
  RawPrograms,
  AllContent,
  RawContentItem,
} from '../../common/bundle.types';
import { BusinessModel, Program } from '@playbook-technologies/common-enums';
import {
  allowedContentTypes,
  toSellableContent,
} from '../../common/bundles.mapper';
import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { useUser } from '@client/new/hooks/use-user';
import { useCustomizations } from '@client/new/hooks/use-customizations';

export type BundlesModel = {
  creatorId: number;
  creatorName: string | undefined;
  isUnifiedProgramsEnabled: boolean;
  customURL: string;
  subscriptionPrograms: AllPrograms;
  allFeatures: AllFeatures;
  sellableContent: AllContent;
  initialSubscriptionBundles: SubscriptionBundle[];
  bundleBusinessModel: boolean;
  hideSubscriptionBundles: boolean;
  discounts: GetSubscriptionBundlesQuery['getCouponsByInfluencer'];
  updateBundlePrices: (
    input: UpsertOrArchiveSubscriptionBundlePricesInput,
  ) => Promise<FetchResult<UpsertOrArchiveSubscriptionBundlePricesMutation>>;
  updateBundleContent: (
    input: UpdateBundleInput | UpdateBundleInput[],
  ) => Promise<FetchResult<UpdateSubscriptionBundlesMutation>>;
  enableCheckoutFreeTrial: boolean;
  refetch: (
    variables?:
      | Partial<
          Exact<{
            creatorId: Scalars['Int'];
            bundlePricing?: InputMaybe<Array<BundlePricing> | BundlePricing>;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetSubscriptionBundlesQuery>>;
  refetchInfluencer: (
    variables?:
      | Partial<
          Exact<{
            id: Scalars['Int'];
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<GetInfluencerQuery>>;
  publishBundle: (
    bundleId: number,
  ) => Promise<FetchResult<PublishSubscriptionBundleMutation>>;
};

export const useSubscriptionBundlesModel = ({
  creatorId,
}: {
  creatorId: number;
}): Async<BundlesModel> => {
  const { refetchInfluencer } = useUser(creatorId);
  const { isLoading: isCustomizationsLoading, customizations } =
    useCustomizations(String(creatorId));
  const { data, loading, error, refetch } = useGetSubscriptionBundlesQuery({
    variables: {
      creatorId,
      bundlePricing: BundlePricing.Subscription,
    },
    fetchPolicy: 'network-only',
  });

  const { data: contentData } = useGetSellableContentItemsQuery({
    variables: {
      creatorId: String(creatorId),
    },
    fetchPolicy: 'network-only',
  });

  const [updateBundlesContentMutation] = useUpdateSubscriptionBundlesMutation();
  const [updateBundlePricesMutation] =
    useUpsertOrArchiveSubscriptionBundlePricesMutation();
  const [publishSubscriptionBundle] = usePublishSubscriptionBundleMutation();

  const initialSubscriptionBundles = useMemo(
    () =>
      toSubscriptionBundles((data?.bundle as RawSubscriptionBundle[]) || []),
    [data?.bundle],
  );

  if (loading || isCustomizationsLoading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch bundle deatils',
    };
  }

  const rawContent = (contentData?.content?.contentItems || []).filter((item) =>
    allowedContentTypes.includes(item.type),
  ) as RawContentItem[];

  const updateBundleContent = (
    input: Array<UpdateBundleInput> | UpdateBundleInput,
  ) =>
    updateBundlesContentMutation({
      variables: {
        input: input,
      },
    });

  const updateBundlePrices = (
    input: UpsertOrArchiveSubscriptionBundlePricesInput,
  ) =>
    updateBundlePricesMutation({
      variables: {
        input: input,
      },
    });

  const publishBundle = (bundleId: number) =>
    publishSubscriptionBundle({
      variables: {
        bundleId,
      },
    });

  return {
    type: 'fetched',
    value: {
      creatorId,
      isUnifiedProgramsEnabled:
        customizations?.isUnifiedProgramsEnabled ?? false,
      creatorName: data?.creator?.name || undefined,
      initialSubscriptionBundles,
      subscriptionPrograms: toSubscriptionPrograms(
        data?.creator?.programs.filter(
          (p) =>
            customizations?.isUnifiedProgramsEnabled ||
            p.accessType === Program.AccessType.Premium.value,
        ) as RawPrograms,
      ),
      sellableContent: rawContent ? toSellableContent(rawContent) : [],
      allFeatures: data?.feature || [],
      bundleBusinessModel:
        data?.creator?.businessModel === BusinessModel.Bundle,
      hideSubscriptionBundles: data?.creator?.visibility === 'limited',
      discounts: data?.getCouponsByInfluencer || [],
      updateBundlePrices,
      updateBundleContent,
      refetch,
      refetchInfluencer,
      publishBundle,
      customURL: data?.creator?.customURL || '',
      enableCheckoutFreeTrial: Boolean(data?.creator?.enableCheckoutFreeTrial),
    },
  };
};
